import {pick} from '@benchling/js-utils';

import {DataCollaborator} from 'auth/PermissionConstants';
import {pluralize} from 'BenchlingUtil';
import {getPrefixToTypename} from 'graphql-client/APIIDPrefixToTypename';
import {writeFragmentsToCache} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExcludeInaccessible from 'graphql-client/ExcludeInaccessible';
import getTypenameFromID from 'graphql-client/getTypenameFromID';
import notifications from 'notifications';
import {BenchlingAction} from 'redux-stores/Action';
import {getStore, Thunk} from 'redux-stores/Store';
import * as LoadingScreen from 'ui-library/LoadingScreen';
import {fetchRecentWorklistsQuery, updateRecentWorklistIdsCache} from 'worklists/useRecentWorklistsQuery';
import {
  forceRefreshWorklistItemsListing,
  forceRefreshWorklistsListing,
  openWorklistListing,
} from 'worklists/WorklistUIActions';
import {mostRecentIds} from 'worklists/WorklistUtil';

import {OwnerAndCollaboratorInfoGraphQL} from '../../graphql-fragments/auth/__generated/OwnerAndCollaboratorInfoGraphQL';
import {
  WorklistItemsGraphQL,
  WorklistItemsGraphQL_WorklistNode_items as WorklistItemNode,
} from '../../graphql-fragments/worklists/__generated/WorklistItemsGraphQL';
import {WorklistSummaryGraphQL} from '../../graphql-fragments/worklists/__generated/WorklistSummaryGraphQL';
import {WorklistSummariesById, WorklistType, WorklistWithOwnerAndCollaborators} from './WorklistConstants';
import * as WorklistDAO from './WorklistDAO';

// After a worklist operation (e.g., "Add to worklist"), what might have changed?
// Setting these flags will trigger API calls to ensure the app stays in sync.
export interface RefreshOptions {
  refreshWorklistItemsSearch?: boolean;
  refreshWorklistsSearch?: boolean;
  refreshRecentWorklists?: boolean;
}

const defaultRefreshOptions = {
  refreshWorklistItemsSearch: false,
  refreshWorklistsSearch: false,
  refreshRecentWorklists: false,
};

export interface UpdateWorklistParams {
  worklistName?: string;
  itemIds?: Array<string>;
  collaborators?: Array<DataCollaborator>;
}

function _doRefreshes(options: RefreshOptions): void {
  if (options.refreshWorklistsSearch) {
    forceRefreshWorklistsListing();
  }
  if (options.refreshWorklistItemsSearch) {
    forceRefreshWorklistItemsListing();
  }
  if (options.refreshRecentWorklists) {
    fetchRecentWorklistsQuery();
  }
}

export function updateWorklists(worklistSummariesById: WorklistSummariesById): void {
  writeFragmentsToCache(
    getFragmentWrapper('WorklistSummary'),
    Object.values(worklistSummariesById).map(
      (worklistSummary) =>
        ({
          __typename: 'WorklistNode',
          ...pick(worklistSummary, 'id', 'name', 'itemType', 'ownerJson', 'modifiedAt'),
          permissions: {
            __typename: 'PermissionsNode',
            ...pick(worklistSummary.permissions, 'admin', 'appendable', 'readable', 'writable'),
            owner: worklistSummary.permissions.owner ?? null,
          },
        }) satisfies WorklistSummaryGraphQL
    )
  );
}

/**
 * Create a worklist with optional items and update relevant redux state. Optionally takes in
 * an onCreateWorklist callback, since dispatching a thunk means you can't await the request and do something
 * after the result.
 */
export function dispatchCreateWorklist(
  worklistName: string,
  worklistItemType: WorklistType,
  itemIds: Array<string>,
  refreshOptions: RefreshOptions = defaultRefreshOptions
): void {
  getStore().dispatch(createWorklist(worklistName, worklistItemType, itemIds, refreshOptions));
}

function createWorklist(
  worklistName: string,
  worklistType: WorklistType,
  itemIds: Array<string>,
  refreshOptions: RefreshOptions = defaultRefreshOptions
): Thunk<void> {
  return async (dispatch) => {
    const newWorklist = await createWorklistAsync(
      worklistName,
      worklistType,
      itemIds,
      refreshOptions,
      null,
      dispatch
    );
    notifications.success(
      `Worklist '${worklistName}' was created${
        itemIds.length > 0 ? ` with ${itemIds.length} ${pluralize(itemIds.length, 'item')}` : ''
      }`,
      {
        duration: 0,
        actionText: 'Open',
        onActionClick: () => {
          openWorklistListing(newWorklist);
        },
      }
    );
  };
}

function writeWorklistToCache(worklist: WorklistWithOwnerAndCollaborators): void {
  writeFragmentsToCache(getFragmentWrapper('OwnerAndCollaboratorInfo'), [
    {
      __typename: 'WorklistNode',
      id: worklist.id,
      name: worklist.name,
      ownerJson: worklist.owner,
      ownerInfoJson: worklist.ownerInfo,
      collaboratorsJson: worklist.collaborators,
    } satisfies OwnerAndCollaboratorInfoGraphQL,
  ]);
}

function writeWorklistItemsToCache(worklistId: string, itemIds: Array<string>): void {
  const apiIdPrefixToTypename = getPrefixToTypename();
  writeFragmentsToCache(getFragmentWrapper('WorklistItems'), [
    {
      __typename: 'WorklistNode',
      id: worklistId,
      items: itemIds.map(
        (id) =>
          ({
            __typename: getTypenameFromID(apiIdPrefixToTypename, id),
            id,
          }) as ExcludeInaccessible<WorklistItemNode>
      ),
    } satisfies WorklistItemsGraphQL,
  ]);
}

export async function createWorklistAsync(
  worklistName: string,
  worklistType: WorklistType,
  itemIds: Array<string>,
  refreshOptions: RefreshOptions = defaultRefreshOptions,
  collaborators: Array<DataCollaborator> | null,
  dispatch: (action: BenchlingAction | Thunk<void>) => void
): Promise<WorklistWithOwnerAndCollaborators> {
  const response = await WorklistDAO.createWorklist(worklistName, worklistType, itemIds, collaborators);

  _doRefreshes(refreshOptions);
  updateWorklists(response.worklistSummaries);
  updateRecentWorklistIdsCache(mostRecentIds(response.worklistSummaries));
  writeWorklistToCache(response.newWorklist);
  return response.newWorklist;
}

export function dispatchUpdateWorklist(
  worklistId: string,
  data: UpdateWorklistParams,
  refreshOptions: RefreshOptions = defaultRefreshOptions
): void {
  getStore().dispatch(updateWorklist(worklistId, data, refreshOptions));
}

function updateWorklist(
  worklistId: string,
  data: UpdateWorklistParams,
  refreshOptions: RefreshOptions = defaultRefreshOptions
): Thunk<void> {
  return async (dispatch) => {
    await updateWorklistAsync(worklistId, data, refreshOptions, dispatch);
    notifications.success(`Worklist was updated`);
  };
}

export async function updateWorklistAsync(
  worklistId: string,
  data: UpdateWorklistParams,
  refreshOptions: RefreshOptions = defaultRefreshOptions,
  dispatch: (action: BenchlingAction | Thunk<void>) => void
): Promise<void> {
  const response = await WorklistDAO.updateWorklist(worklistId, data);

  _doRefreshes(refreshOptions);
  updateWorklists({[worklistId]: response});
  updateRecentWorklistIdsCache(mostRecentIds({[worklistId]: response}));
  writeWorklistItemsToCache(worklistId, response.itemIds);
  writeWorklistToCache(response);
}

export function dispatchBulkDeleteWorklists(
  worklistIds: Array<string>,
  options: RefreshOptions = defaultRefreshOptions
): void {
  getStore().dispatch(bulkDeleteWorklists(worklistIds, options));
}

export function bulkDeleteWorklists(worklistIds: Array<string>, options: RefreshOptions): Thunk<void> {
  return async (dispatch) => {
    LoadingScreen.show('Deleting worklists...');
    try {
      const response = await WorklistDAO.bulkDeleteWorklists(worklistIds);
      notifications.success(`Deleted ${worklistIds.length} ${pluralize(worklistIds.length, 'worklist')}`);
      updateWorklists(response);
      updateRecentWorklistIdsCache(mostRecentIds(response));
      _doRefreshes(options);
    } finally {
      LoadingScreen.hide();
    }
  };
}

export function dispatchAddWorklistItems(
  worklistId: string,
  itemIds: Array<string>,
  options: RefreshOptions = defaultRefreshOptions
): void {
  getStore().dispatch(addWorklistItems(worklistId, itemIds, options));
}

function addWorklistItems(worklistId: string, itemIds: Array<string>, options: RefreshOptions): Thunk<void> {
  return async (dispatch) => {
    const worklist = await WorklistDAO.addItemsToWorklist(worklistId, itemIds);
    const numItems = itemIds.length;
    notifications.success(
      `${numItems} ${pluralize(numItems, 'item')} ${pluralize(numItems, 'was', 'were')} added to worklist '${
        worklist.name
      }'.`
    );
    writeWorklistItemsToCache(worklistId, worklist.itemIds);
    _doRefreshes(options);
  };
}

export function dispatchRemoveWorklistItems(
  worklistId: string,
  itemIds: Array<string>,
  options: RefreshOptions = defaultRefreshOptions
): void {
  getStore().dispatch(removeWorklistItems(worklistId, itemIds, options));
}

export function removeWorklistItems(
  worklistId: string,
  itemIds: Array<string>,
  options: RefreshOptions
): Thunk<void> {
  return async (dispatch) => {
    LoadingScreen.show('Removing items...');
    try {
      const worklist = await WorklistDAO.removeItemsFromWorklist(worklistId, itemIds);
      const numItems = itemIds.length;
      notifications.success(
        `${numItems} ${pluralize(numItems, 'item')} ${pluralize(
          numItems,
          'was',
          'were'
        )} removed from worklist '${worklist.name}'.`
      );
      writeWorklistItemsToCache(worklistId, worklist.itemIds);
      _doRefreshes(options);
    } finally {
      LoadingScreen.hide();
    }
  };
}

export function removeWorklistUnauthorizedItems(worklistId: string): Thunk<void> {
  return async (dispatch) => {
    const newWorklist = await WorklistDAO.removeUnauthorizedItemsFromWorklist(worklistId);
    writeWorklistItemsToCache(newWorklist.id, newWorklist.itemIds);
    // TODO: Update the most recent worklists home listing data here.
  };
}
